import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Id } from '../../api/types';
import { PROJECT_PARAM } from '../../components/layout/nav-links';

/**
 * Project and selected ticket in the URL — "the APFS releases page" is a link people send, and Back
 * out of a ticket must return to the version you were reading rather than to a default project.
 *
 * ⛔ The project is READ here and set by the shell's rail. One control per piece of state.
 */
export type VersionsTab = 'versions' | 'history';

export function useVersionsParams() {
  const [params, setParams] = useSearchParams();

  const write = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(params);
      mutate(next);
      setParams(next);
    },
    [params, setParams],
  );

  /**
   * ⭐ THE TAB IS IN THE URL. "Send me the release history for APFS" is a link, and this repo already
   * puts the backlog's filters, its expanded rows and the board's lane there for exactly that reason.
   * ⚠️ `replace` — switching tab is a change of VIEW, not of place; pushing would make Back walk
   * through the tabs instead of leaving the page.
   */
  const setTab = useCallback(
    (tab: VersionsTab) => {
      const next = new URLSearchParams(params);
      if (tab === 'versions') next.delete('tab');
      else next.set('tab', tab);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const select = useCallback(
    (ticketId: Id | null) => {
      write((next) => {
        if (ticketId) next.set('ticket', ticketId);
        else next.delete('ticket');
      });
    },
    [write],
  );

  return {
    // ⭐ The shell's key, imported not retyped — the rail writes it, this page reads it.
    projectId: params.get(PROJECT_PARAM),
    selectedId: params.get('ticket'),
    // ⚠️ An unrecognised `?tab=` falls back to the version list rather than rendering nothing.
    tab: params.get('tab') === 'history' ? ('history' as const) : ('versions' as const),
    setTab,
    select,
  };
}
